// Platform-admin-only card: list dealers + create dialog.
// Dealer-owners don't see this card (App.js hides it based on role).
import React, { useEffect, useState } from 'react';
import { Building2, Phone, Plus, RefreshCw, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { dealersApi } from '../../api';
import DealerOnboardingDialog from './dealer-onboarding/DealerOnboardingDialog';
import InboundNumbersDialog from './InboundNumbersDialog';

const EMPTY_FORM = { name: '', dealer_type: 'local_retail', status: 'active' };

const DealersCard = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [configuringId, setConfiguringId] = useState(null);
  const [phoneEditDealer, setPhoneEditDealer] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setDealers(await dealersApi.list());
    } catch (err) {
      toast.error(err.message || 'Failed to load dealers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Dealer name is required');
      return;
    }
    setSaving(true);
    try {
      await dealersApi.create(form);
      toast.success(`Created ${form.name}`);
      setOpen(false);
      setForm(EMPTY_FORM);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to create dealer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-card border-white/20" data-testid="dealers-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          <div>
            <CardTitle>Dealers</CardTitle>
            <CardDescription className="mt-1">Manage dealership tenants</CardDescription>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} data-testid="dealers-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="dealers-create-btn">
                <Plus className="w-4 h-4" /> New Dealer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create dealer</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="dealer-name">Name</Label>
                  <Input
                    id="dealer-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="dealer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={form.dealer_type}
                    onValueChange={(v) => setForm({ ...form, dealer_type: v })}
                  >
                    <SelectTrigger data-testid="dealer-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local_retail">Local retail</SelectItem>
                      <SelectItem value="broker_remote">Broker / remote</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button onClick={handleCreate} disabled={saving} data-testid="dealer-save-btn">
                  {saving ? 'Saving…' : 'Create dealer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : dealers.length === 0 ? (
          <div className="text-sm text-muted-foreground">No dealers yet. Create the first one above.</div>
        ) : (
          <div className="divide-y divide-border rounded-md border">
            {dealers.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3 gap-3" data-testid={`dealer-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.dealer_type.replace('_', ' ')} · onboarding: {d.onboarding_status || 'draft'} · {d.id}
                  </div>
                  {Array.isArray(d.inbound_numbers) && d.inbound_numbers.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Inbound: {d.inbound_numbers.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                    d.status === 'active' ? 'bg-green-500/10 text-green-600' :
                    d.status === 'paused' ? 'bg-yellow-500/10 text-yellow-600' :
                    'bg-red-500/10 text-red-600'
                  }`}>{d.status}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => setPhoneEditDealer(d)}
                    data-testid={`dealer-phone-${d.id}`}
                    title="Manage inbound phone numbers"
                  >
                    <Phone className="w-3.5 h-3.5" /> Numbers
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => setConfiguringId(d.id)}
                    data-testid={`dealer-configure-${d.id}`}
                  >
                    <Settings className="w-3.5 h-3.5" /> Configure
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {configuringId && (
        <DealerOnboardingDialog
          dealerId={configuringId}
          open={Boolean(configuringId)}
          onOpenChange={(v) => { if (!v) setConfiguringId(null); }}
          onChanged={refresh}
        />
      )}
      {phoneEditDealer && (
        <InboundNumbersDialog
          dealer={phoneEditDealer}
          open={Boolean(phoneEditDealer)}
          onOpenChange={(v) => { if (!v) setPhoneEditDealer(null); }}
          onSaved={async () => {
            setPhoneEditDealer(null);
            await refresh();
          }}
        />
      )}
    </Card>
  );
};

export default DealersCard;
